import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, effect, inject, untracked } from '@angular/core';

import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { Language, TranslationService, isLanguage } from './i18n/translation.service';

/**
 * The language the user works in is the server's truth (notifications, emails):
 * at sign-in the saved preference is read once and shown, and only a later switch
 * by the user is saved. A language the app cannot show (pt, fr) or none shows
 * Spanish and is left as it is on the server.
 */
@Injectable({ providedIn: 'root' })
export class LanguageSyncService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private translation = inject(TranslationService);
  private injector = inject(Injector);
  /** What the server holds for the signed-in user; undefined until read */
  private saved: string | undefined;
  /** The user whose preference was read; undefined makes the next change read it again */
  private userId: number | undefined;

  start(): void {
    effect(
      () => {
        const user = this.auth.currentUser();
        const language = this.translation.getCurrentLanguage()();
        untracked(() => this.sync(user?.id, language));
      },
      { injector: this.injector }
    );
  }

  private sync(userId: number | undefined, language: Language): void {
    if (userId == null) {
      this.userId = undefined;
      this.saved = undefined;
      return;
    }
    if (userId !== this.userId) {
      this.userId = userId;
      this.saved = undefined;
      this.http
        .get<{ language?: string }>(`${environment.apiBaseUrl}/users/preferences/`)
        .subscribe({
          next: (prefs) => {
            if (this.userId !== userId) return; // signed out or another user meanwhile
            // The server wins over this browser's last language: on a shared computer
            // that is the previous user's, and on this device it may be out of date
            const language = isLanguage(prefs.language) ? prefs.language : 'es';
            this.saved = language;
            this.translation.setLanguage(language);
          },
          error: () => this.forget(userId),
        });
      return;
    }
    if (this.saved !== undefined) this.save(language);
  }

  private save(language: Language): void {
    if (language === this.saved) return;
    this.saved = language;
    const userId = this.userId;
    this.http
      .patch(`${environment.apiBaseUrl}/users/update_preferences/`, { language })
      .subscribe({ error: () => this.forget(userId) });
  }

  /** After a failed request the next change reads the preference again */
  private forget(userId: number | undefined): void {
    if (this.userId !== userId) return;
    this.userId = undefined;
    this.saved = undefined;
  }
}
